import { Suspense, useCallback, useRef } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useTranslation } from "react-i18next"
import { useLazyLoadQuery } from "react-relay"
import { X } from "@phosphor-icons/react/dist/ssr"
import { graphql } from "relay-runtime"
import { Button } from "@/components/ui/button"
import { Drawer, DrawerBody, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer"
import { useClickOutside } from "@/hooks/useClickOutside"
import { externalAssistanceDrawerQuery } from "@/modules/patients/agenda/external-assistance/__generated__/externalAssistanceDrawerQuery.graphql"
import { ErrorFallback } from "@/modules/waiting-room/components/appointment-drawer/appointment-drawer-error-fallback"

const chExternalAssistanceDrawerQuery = graphql`
  query externalAssistanceDrawerQuery($id: ID!) {
    ch_externalAssistanceById(id: $id) {
      reason
    }
  }
`

const ExternalAssistanceDrawer = ({
  selectedExternalAssistanceId,
}: {
  selectedExternalAssistanceId: string
  onClose?: () => void
}) => {
  const drawerRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()

  const { ch_externalAssistanceById: externalAssistanceData } = useLazyLoadQuery<externalAssistanceDrawerQuery>(
    chExternalAssistanceDrawerQuery,
    {
      id: selectedExternalAssistanceId,
    },
  )

  useClickOutside((event: MouseEvent) => {
    if (drawerRef.current && !drawerRef.current.contains(event.target as HTMLElement)) {
      closeDrawer()
    }
  }, drawerRef)

  const closeDrawer = () => {
    handleToggleDrawer(true)
  }

  const handleToggleDrawer = useCallback(
    (close = false) => {
      if (drawerRef.current) {
        if (close) {
          drawerRef.current.classList.remove("w-125")
          drawerRef.current.classList.add("hidden", "w-0")
        } else {
          drawerRef.current.classList.add("w-125")
          drawerRef.current.classList.remove("hidden", "w-0")
        }
      }
    },
    [drawerRef],
  )

  return (
    <Drawer open>
      <div className="duration-300">
        <DrawerContent
          className="shadow-1 absolute right-0 z-12 border"
          ref={drawerRef}
          onOpenAutoFocus={(e) => e.preventDefault()}>
          <ErrorBoundary fallback={<ErrorFallback onClose={closeDrawer} />}>
            <Suspense>
              <DrawerHeader
                className="animate-fade-in-bottom flex flex-col gap-4"
                data-testid="agenda-appointment-drawer-head">
                <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued mb-15 pr-0 text-sm font-medium">
                  <div className="flex flex-row items-center justify-between">
                    <p>{t("patient.create.callReason.externalAssistance.title")}</p>
                    <Button onClick={closeDrawer} variant="circle" className="h-10 w-10 shrink-0 self-end p-2">
                      <X />
                    </Button>
                  </div>
                </DrawerTitle>
              </DrawerHeader>
              <DrawerBody data-testid="agenda-appointment-drawer-body">
                <div className="flex flex-col items-start justify-start gap-4">
                  <h2 className="text-lg font-semibold">{t("patient.agenda.grid.columnName.reasonForEncounter")}</h2>
                  <p className="text-sm">{externalAssistanceData?.reason}</p>
                </div>
              </DrawerBody>
            </Suspense>
          </ErrorBoundary>
        </DrawerContent>
      </div>
    </Drawer>
  )
}

export { ExternalAssistanceDrawer }
